/* Progressive interactions for the existing static storefront. */
(() => {
  "use strict";
  const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let headerObserver;
  let observedHeader;
  const alignHeader = () => {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const update = () => document.documentElement.style.setProperty('--sticky-header-offset', `${Math.ceil(header.getBoundingClientRect().height) + 16}px`);
    update();
    if (observedHeader !== header && typeof ResizeObserver === 'function') {
      headerObserver?.disconnect();
      headerObserver = new ResizeObserver(update);
      headerObserver.observe(header);
      observedHeader = header;
    }
  };
  let spinFrame = null;
  let spinningAnimation = null;
  const resetSpin = () => {
    if (spinFrame !== null) cancelAnimationFrame(spinFrame);
    spinFrame = null;
    spinningAnimation?.updatePlaybackRate(1);
    spinningAnimation = null;
  };
  const boostSeal = seal => {
    if (reducedMotion()) return;
    const animation = seal.querySelector('svg')?.getAnimations?.().find(item => item.animationName === 'pbGraphicSpin');
    if (!animation) return;
    if (spinFrame !== null) cancelAnimationFrame(spinFrame);
    let rate = Math.min(24, Math.max(1, animation.playbackRate) + 4);
    animation.updatePlaybackRate(rate);
    spinningAnimation = animation;
    let previous = null;
    const ease = now => {
      if (!seal.isConnected || reducedMotion()) { resetSpin(); return; }
      if (previous !== null) rate = 1 + (rate - 1) * Math.exp(-1.25 * Math.min((now - previous) / 1000, .1));
      previous = now;
      animation.updatePlaybackRate(rate < 1.015 ? 1 : rate);
      if (rate < 1.015) { spinFrame = null; spinningAnimation = null; }
      else spinFrame = requestAnimationFrame(ease);
    };
    spinFrame = requestAnimationFrame(ease);
  };
  const closeMenu = () => document.querySelectorAll(".mobile-menu[open]").forEach(el => { el.open = false; });
  const scrollToHash = (hash, moveFocus = false, smooth = true) => {
    alignHeader();
    let id;
    try { id = decodeURIComponent(hash.slice(1)); } catch (_) { return false; }
    const target = document.getElementById(id);
    if (!target) return false;
    closeMenu();
    target.scrollIntoView({ behavior: smooth && !reducedMotion() ? "smooth" : "instant", block: "start" });
    if (moveFocus) {
      const focusTarget = id === "top" ? document.querySelector(".brand") : target;
      if (focusTarget) {
        if (!focusTarget.hasAttribute("tabindex") && !focusTarget.matches("a,button,input,summary")) focusTarget.setAttribute("tabindex", "-1");
        focusTarget.focus({ preventScroll: true });
      }
    }
    return true;
  };
  const pagePath = pathname => pathname.replace(/\/index(?:\.html)?$/, "/").replace(/\.html$/, "").replace(/\/$/, "") || "/";
  const followSectionLink = (event, link) => {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === "_blank" || link.hasAttribute("download")) return false;
    const url = new URL(link.href, location.href);
    if (!url.hash || url.origin !== location.origin || pagePath(url.pathname) !== pagePath(location.pathname) || url.search !== location.search) return false;
    if (!scrollToHash(url.hash, true)) return false;
    event.preventDefault();
    if (location.hash !== url.hash) history.pushState(null, "", location.pathname + location.search + url.hash);
    return true;
  };
  const copyEmail = async button => {
    const feedback = document.getElementById("contact-feedback");
    button.disabled = true;
    try {
      await navigator.clipboard.writeText("hello@pinkbirdsalt.com");
      if (feedback) feedback.textContent = "Email address copied. Paste it into your email app to write to Alex.";
    } catch (_) {
      if (feedback) feedback.textContent = "Copy is unavailable in this browser. Select hello@pinkbirdsalt.com above and copy it, or choose Email Alex.";
    } finally {
      button.disabled = false;
    }
  };
  const changeTab = (button, moveFocus = false) => {
    const section = button.closest("[data-use-section]");
    if (!section) return;
    section.querySelectorAll("[data-use-tab]").forEach(tab => {
      const selected = tab === button;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute("aria-controls"));
      if (panel) panel.hidden = !selected;
    });
    if (moveFocus) button.focus();
  };
  document.addEventListener("click", event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    const seal = target.closest('[data-spin-seal]');
    if (seal) { boostSeal(seal); return; }
    const copyButton = target.closest("[data-copy-email]");
    if (copyButton) { copyEmail(copyButton); return; }
    const sectionLink = target.closest("a[href]");
    if (sectionLink && followSectionLink(event, sectionLink)) return;
    const tab = target.closest("[data-use-tab]");
    if (tab) { changeTab(tab); return; }
    if (target.closest(".mobile-menu a")) closeMenu();
    if (!target.closest(".mobile-menu") && document.querySelector(".mobile-menu[open]")) closeMenu();
    if (target instanceof HTMLDialogElement && target.open) {
      const box = target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) target.close();
    }
  });
  document.addEventListener("keydown", event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (event.key === "Escape") {
      const openMenu = document.querySelector(".mobile-menu[open]");
      if (openMenu) { closeMenu(); openMenu.querySelector("summary")?.focus(); }
    }
    if (target.matches("[data-use-tab]") && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const tabs = Array.from(target.closest('[role="tablist"]').querySelectorAll("[data-use-tab]"));
      const index = tabs.indexOf(target);
      const next = event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      changeTab(tabs[next], true);
    }
  });
  const mount = () => {
    alignHeader();
    const hash = location.hash;
    if (hash) requestAnimationFrame(() => { scrollToHash(hash, false, false); });
  };
  window.addEventListener("hashchange", () => scrollToHash(location.hash, false, false));
  window.PinkBird = { mount };
  document.addEventListener("shopify:section:load", mount);
  document.addEventListener("DOMContentLoaded", mount, { once: true });
})();

